/**
 * アイコン(.Icon)の心拍ホバー(1回のみ)を共通コンテナに仕込む。
 * ホバー入りで「アイコンの塗り菱形が1回スライド」＋（.Btnのみ）「テキスト1拍」を再生し、
 * ループせず1サイクルで終了する。背景色・枠線・画像などの変化はCSS transition側で担当。
 * 参照: addict_intraction / button-heartbeat-once・card-row-link-trio・card-image-hover-trio
 * 対象: .Btn,.Btn2,.service__catch,.Sidenav__top,.newsShare__listItem（テキスト1拍＋アイコンスライドあり／newsShareのアイコンは .newsShare__listItemIcon）/ .CardNext / .Case / .Servicedetail / .News / .Position / .Pagination .prev,.next / ヘッダー・フッターのONEVIEW別タブリンク（内部の .Icon）
 * さらに .Btnと同じ「文字ぼかし1拍」だけを持つリンク/ピル/ナビ類（.fv__news a / .works__filterItem / .Sidenav__listItemLink / .newsCat__listItem / .Pagination / PCヘッダーの .header__navListItemLink・.header__navCtaBtn・SERVICEプルダウンの .header__pulldownTtl・.header__pulldownListItemLink / SPドロワーの .header__drawerListItemLink・.header__drawerSubLink・.header__drawerOneview / パンくず .Breadcrumb a）にも文字要素だけに当てる。
 *
 * swup対応: <main>内の対象は毎ページ mountIconHoverInScope(scope) で仕込み直す（AbortControllerで解除）。
 * header/footer（main外・永続）の対象は mountIconHoverPersistent() で初回1回だけ仕込む
 * （毎遷移で再バインドすると1ホバー2拍になるため）。DOM位置(<main>内/外)で振り分けるので、
 * .Btn のように main と footer 両方に出るセレクタでも二重付与しない。
 */

import type { PageModule } from "./page-module"

/** テキスト1拍の総尺(ms)：0.18(沈み) + 0.14(戻り) */
const TEXT_DURATION = 320
/** アイコン1スライドの総尺(ms)：0.08 + 0.08 + 0.54 */
const ICON_DURATION = 700

/**
 * テキストを1回だけドクンと脈打たせる（不透明度＋ぼかし）。
 * 各キーフレームの easing はそのキーフレーム→次区間に適用。power2.in / expo.out を近似。
 */
const TEXT_KEYFRAMES: Keyframe[] = [
  { opacity: 1, filter: "blur(0px)", offset: 0, easing: "cubic-bezier(0.55, 0.085, 0.68, 0.53)" },
  { opacity: 0.6, filter: "blur(4px)", offset: 0.5625, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  { opacity: 1, filter: "blur(0px)", offset: 1 },
]

/**
 * 塗り菱形だけを1回スライドさせて戻す（アウトラインはCSSの逆補正で静止＝参考デモ準拠）。
 * カスタムプロパティ --icon-x を駆動し、power4.out / sine.inOut / expo.out を近似。
 */
const ICON_KEYFRAMES: Keyframe[] = [
  { "--icon-x": "0px", offset: 0, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
  { "--icon-x": "5px", offset: 0.114, easing: "cubic-bezier(0.37, 0, 0.63, 1)" },
  { "--icon-x": "3px", offset: 0.229, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  { "--icon-x": "0px", offset: 1 },
]

/**
 * 指定要素の「テキスト1拍＋アイコン1スライド」を1回再生する。
 * ホバー以外（別要素ホバーとの同期発火：SERVICE画像↔キャッチ等）からも呼べるよう切り出し。
 * @param {HTMLElement} el - 対象コンテナ（内部の text/icon を querySelector で探す）
 * @param {string | null} textSel - 拍動させる文字要素セレクタ（null=アイコンのみ）
 * @param {string} iconSel - スライドさせるアイコンセレクタ（既定 .Icon）
 */
export const playHeartbeat = (el: HTMLElement, textSel: string | null = "p", iconSel = ".Icon"): void => {
  const icon = el.querySelector<HTMLElement>(iconSel)
  const text = textSel ? el.querySelector<HTMLElement>(textSel) : null

  if (text) {
    text.style.willChange = "opacity, filter"
    text.animate(TEXT_KEYFRAMES, { duration: TEXT_DURATION, fill: "none" })
  }
  // 拍動中の再トリガーは無視（ループ/重ねがけ防止＝1回のみ）
  if (icon && !icon.getAnimations().some((a) => a.playState === "running")) {
    icon.animate(ICON_KEYFRAMES, { duration: ICON_DURATION, fill: "none" })
  }
}

/**
 * 1つのコンテナに心拍ホバーを仕込む
 * @param {HTMLElement} el - ホバー対象（.Btn / .Sidenav__top / .CardNext / .Case / .prev / .next 等）
 * @param {string | null} textSel - テキスト1拍させる文字要素のセレクタ（.Btn等=<p> / .Sidenav__top=.Sidenav__topTxt）。null=アイコンのみ
 * @param {AbortSignal} [signal] - 解除用シグナル（main内=毎ページ張替えのため付与／header/footer永続=省略）
 * @param {string} [iconSel] - スライドさせるアイコンのセレクタ（既定 .Icon／newsShareは .newsShare__listItemIcon）
 */
const setupIconHeartbeat = (el: HTMLElement, textSel: string | null, signal?: AbortSignal, iconSel = ".Icon"): void => {
  const icon = el.querySelector<HTMLElement>(iconSel)
  const text = textSel ? el.querySelector<HTMLElement>(textSel) : null
  if (!icon && !text) return

  let isHovering = false

  /**
   * ホバー開始：（テキスト1拍＋）アイコン1スライドを開始（1ホバーにつき1回）
   */
  const onEnter = (): void => {
    if (isHovering) return
    isHovering = true
    playHeartbeat(el, textSel, iconSel)
  }

  /**
   * ホバー終了：フラグを戻す（進行中の拍は自然完了）
   */
  const onLeave = (): void => {
    isHovering = false
  }

  const opts = signal ? { signal } : undefined
  el.addEventListener("pointerenter", onEnter, opts)
  el.addEventListener("pointerleave", onLeave, opts)
}

/**
 * 「文字ぼかし1拍」だけをコンテナに仕込む（アイコンは無い/別担当のリンク・ピル・ナビ用）。
 * 背景・枠を滲ませないよう、コンテナ自身ではなく指定の文字要素だけに当てる。
 * @param {HTMLElement} el - ホバー検知するコンテナ
 * @param {HTMLElement[]} textEls - 拍動させる文字要素（複数可）
 * @param {AbortSignal} [signal] - 解除用シグナル（main内のみ付与）
 */
const setupTextPulse = (el: HTMLElement, textEls: HTMLElement[], signal?: AbortSignal): void => {
  if (!textEls.length) return

  let isHovering = false

  /** ホバー開始：文字要素を1回だけドクッとぼかす（1ホバーにつき1回） */
  const onEnter = (): void => {
    if (isHovering) return
    isHovering = true
    textEls.forEach((t) => {
      t.style.willChange = "opacity, filter"
      t.animate(TEXT_KEYFRAMES, { duration: TEXT_DURATION, fill: "none" })
    })
  }

  const opts = signal ? { signal } : undefined
  el.addEventListener("pointerenter", onEnter, opts)
  el.addEventListener(
    "pointerleave",
    () => {
      isHovering = false
    },
    opts
  )
}

/**
 * 心拍ホバー（.Icon の塗り1スライド）を仕込むコンテナ群。
 * [コンテナ, 文字要素セレクタ|null] — セレクタ指定時はテキスト1拍も伴う（.Btn等）／null=アイコンのみ（カード類）。
 */
const HEARTBEAT_GROUPS: [string, string | null, string?][] = [
  [".Btn", "p"],
  [".Btn2", "p"],
  [".service__catch", "p"],
  // フォーム(contact/form)の確認/登録/戻るボタン：.Btnと同じ「菱形(.Icon)スライド＋文字1拍」。文字は<span class="Form__btnTxt">
  [".Form__btn", ".Form__btnTxt"],
  // newsShare：名前の文字1拍＋アイコン(.newsShare__listItemIcon)の塗りスライド＝.Btnと同じホバー
  [".newsShare__listItem", ".newsShare__listItemName", ".newsShare__listItemIcon"],
  [".Sidenav__top", ".Sidenav__topTxt"], // 実績一覧を見る：文字(span)＋矢印。.Btnと同じ文字1拍＋アイコン鼓動に
  [".CardNext", null],
  [".Case", null],
  [".Servicedetail", null],
  [".News", null],
  [".Position", null],
  [".Pagination .prev", null],
  [".Pagination .next", null],
  [".header__navListItemLink--oneview", null],
  [".header__drawerOneview", null],
  [".footer__sitemapOneview", null],
]

/**
 * .Btnと同じ「文字ぼかし1拍」を持つリンク/ピル/ナビ。背景を滲ませないよう文字要素だけに当てる。
 * [ホバー検知コンテナ, 文字要素セレクタ(null=コンテナ自身＝背景透明なページ番号リンク用)]
 */
const TEXT_PULSE_GROUPS: [string, string | null][] = [
  [".fv__news a", ".date, h2"],
  [".works__filterItem", ".works__filterItemTxt"],
  [".Sidenav__listItemLink", ".Sidenav__listItemLinkTxt"],
  [".newsCat__listItem", ".newsCat__listItemName, .newsCat__listItemCount"],
  [".Pagination a.page-numbers:not(.prev):not(.next)", null],
  [".Pagination .prev, .Pagination .next", ":scope > span"], // ラベルspanのみ（アイコン内spanは除外）
  // PCナビ: ABOUT/WORKS/NEWS=文字のみ／SERVICE=文字＋下矢印(.Icon)も拍動
  [".header__navListItemLink:not(.header__navListItemLink--oneview)", ".header__navListItemLinkTxt, .Icon"],
  // ONEVIEW: SVGロゴを拍動（別タブアイコンはfill-slide心拍のまま＝拍動対象外）
  [".header__navListItemLink--oneview", ".Oneviewlogo"],
  [".header__navCtaBtn", ".header__navCtaBtnTxt"], // PCナビCTA(CONTACT/RECRUIT)
  // PC SERVICEプルダウン：見出し(ADDICTのサービス＝リンク化)＋子リンクを .Btn と同じ文字1拍で統一
  [".header__pulldownTtl", null],
  [".header__pulldownListItemLink", null],
  // SPドロワー(ハンバーガーメニュー)内リンクも .Btn と同じ文字1拍に
  [".header__drawerListItemLink", ".header__drawerListItemLinkEn, .header__drawerListItemLinkJa"], // 主リンク(ABOUT等)：EN/JA両方を1拍
  [".header__drawerSubLink", null], // SERVICE子リンク(テキスト直)
  [".header__drawerOneview", ".Oneviewlogo"], // ロゴ拍動（別タブアイコンのスライドは上の icon-heartbeat 側）
  // フッターのリンク類も .Btn と同じ文字1拍に統一（旧: 持続ディム opacity/color は廃止）。テキスト直下なのでコンテナ自身を拍動
  [".footer__sitemapColItemLink", null], // 会社概要/実績紹介/ニュース/リクルート/お問い合わせ/事業内容(見出し)
  [".footer__sitemapSubLink", null], // 事業内容の子リンク(SNS広告 等)
  [".footer__copyLink", null], // コピーライト脇のプライバシーポリシー(PC)
  [".footer__sitemapOneview", ".Oneviewlogo"], // ロゴ拍動（別タブアイコンのスライドは上の icon-heartbeat 側＝headerのONEVIEWと同じ）
  // パンくずリスト：各リンク(<a>)を1拍。現在ページの <p> はリンクでないので対象外
  [".Breadcrumb a", null],
]

/**
 * 指定した要素選択関数（pick）配下に心拍ホバーを仕込む共通処理。
 * @param {(sel: string) => HTMLElement[]} pick - セレクタから対象要素配列を返す（scope配下 or main外）
 * @param {AbortSignal} [signal] - 解除用シグナル（main内=付与／永続=省略）
 */
const bindIconHover = (pick: (sel: string) => HTMLElement[], signal?: AbortSignal): void => {
  HEARTBEAT_GROUPS.forEach(([sel, textSel, iconSel]) => {
    pick(sel).forEach((el) => setupIconHeartbeat(el, textSel, signal, iconSel))
  })
  TEXT_PULSE_GROUPS.forEach(([container, textSel]) => {
    pick(container).forEach((el) => {
      const textEls = textSel ? Array.from(el.querySelectorAll<HTMLElement>(textSel)) : [el]
      setupTextPulse(el, textEls, signal)
    })
  })
}

/** reduced-motion 判定（動きを付けない。色/画像の変化はCSS側のみ） */
const prefersReduced = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * <main>内の対象に心拍ホバーを毎ページ仕込む（PageModule）。
 * 遷移で <main> ごと差し替わるため AbortController で解除できるようにする（旧mainの残存中の二重防止）。
 * @param {HTMLElement} scope - 現在ページの <main>
 * @returns {(() => void) | void} リスナーを解除する destroy（reduced-motion時は void）
 */
export const mountIconHoverInScope: PageModule = (scope) => {
  if (prefersReduced()) return
  const ac = new AbortController()
  bindIconHover((sel) => Array.from(scope.querySelectorAll<HTMLElement>(sel)), ac.signal)
  return () => ac.abort()
}

/**
 * header/footer（<main>外・永続）の対象に心拍ホバーを初回1回だけ仕込む。
 * <main>内の要素は除外（mountIconHoverInScope 担当）＝二重付与を防ぐ。永続なので解除しない。
 */
export const mountIconHoverPersistent = (): void => {
  if (prefersReduced()) return
  bindIconHover((sel) =>
    Array.from(document.querySelectorAll<HTMLElement>(sel)).filter((el) => !el.closest("main"))
  )
}
